import { CATEGORIES, SIDES } from "@/lib/shop";

export type Filters = {
  categories: string[];
  brands: string[];
  sizes: string[];
  colours: string[];
  sides: string[];
  inStock: boolean;
  onSale: boolean;
  physioRecommended: boolean;
  maxPrice: number | null;
};

export const emptyFilters: Filters = {
  categories: [],
  brands: [],
  sizes: [],
  colours: [],
  sides: [],
  inStock: false,
  onSale: false,
  physioRecommended: false,
  maxPrice: null,
};

type Facets = { brands: string[]; sizes: string[]; colours: string[]; priceMax: number };

export function ShopFilters({
  filters,
  setFilters,
  facets,
}: {
  filters: Filters;
  setFilters: (f: Filters) => void;
  facets: Facets;
}) {
  const toggle = (key: "categories" | "brands" | "sizes" | "colours" | "sides", value: string) => {
    const cur = filters[key];
    setFilters({ ...filters, [key]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] });
  };

  return (
    <div className="space-y-6 text-sm">
      <Group title="Category">
        <div className="space-y-2">
          {CATEGORIES.map((c) => (
            <div key={c.name}>
              <Check label={c.name} checked={filters.categories.includes(c.name)} onChange={() => toggle("categories", c.name)} />
              {c.children && (
                <div className="ml-4 mt-1 space-y-1">
                  {c.children.map((child) => (
                    <Check
                      key={child}
                      label={child}
                      checked={filters.categories.includes(child)}
                      onChange={() => toggle("categories", child)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Group>

      {facets.brands.length > 0 && (
        <Group title="Brand">
          {facets.brands.map((b) => (
            <Check key={b} label={b} checked={filters.brands.includes(b)} onChange={() => toggle("brands", b)} />
          ))}
        </Group>
      )}

      <Group title="Price">
        <label className="block text-xs text-muted-foreground">
          Up to R{filters.maxPrice ?? facets.priceMax}
          <input
            type="range"
            min={0}
            max={facets.priceMax}
            step={50}
            value={filters.maxPrice ?? facets.priceMax}
            onChange={(e) => setFilters({ ...filters, maxPrice: Number(e.target.value) })}
            className="mt-2 w-full accent-[var(--brand-navy,currentColor)]"
            aria-label="Maximum price"
          />
        </label>
      </Group>

      {facets.sizes.length > 0 && (
        <Group title="Size">
          {facets.sizes.map((s) => (
            <Check key={s} label={s} checked={filters.sizes.includes(s)} onChange={() => toggle("sizes", s)} />
          ))}
        </Group>
      )}

      {facets.colours.length > 0 && (
        <Group title="Colour">
          {facets.colours.map((c) => (
            <Check key={c} label={c} checked={filters.colours.includes(c)} onChange={() => toggle("colours", c)} />
          ))}
        </Group>
      )}

      <Group title="Side">
        {SIDES.map((s) => (
          <Check key={s} label={s} checked={filters.sides.includes(s)} onChange={() => toggle("sides", s)} />
        ))}
      </Group>

      <Group title="Availability">
        <Check label="In stock only" checked={filters.inStock} onChange={() => setFilters({ ...filters, inStock: !filters.inStock })} />
        <Check label="On sale" checked={filters.onSale} onChange={() => setFilters({ ...filters, onSale: !filters.onSale })} />
        <Check
          label="Physiotherapist recommended"
          checked={filters.physioRecommended}
          onChange={() => setFilters({ ...filters, physioRecommended: !filters.physioRecommended })}
        />
      </Group>

      <button
        type="button"
        onClick={() => setFilters(emptyFilters)}
        className="w-full rounded-full border border-border px-4 py-2 text-xs font-semibold text-primary hover:bg-accent"
      >
        Clear all filters
      </button>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-primary/70">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-xs text-foreground/80 hover:text-primary">
      <input type="checkbox" checked={checked} onChange={onChange} className="h-3.5 w-3.5 rounded border-border" />
      {label}
    </label>
  );
}
